import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../../services/apiService";
import { PortfolioCompany } from "../../types/portfolio";
import InlineSpinner from "../common/InlineSpinner";
import InviteCompanyForm from "./InviteCompanyForm";
import CompanyDetailModal from "./CompanyDetailModal";
import { useToast, ToastContainer } from "../common/Toast";
import { getBaseUrl } from "../../utils/subdomainDetection";
import styles from "./VCPortfolioPage.module.css";

interface VCPortfolioPageProps {
  companies: PortfolioCompany[];
  loading: boolean;
  error: string | null;
  onReload: () => void;
}

const VCPortfolioPage: React.FC<VCPortfolioPageProps> = ({ 
  companies, 
  loading, 
  error, 
  onReload 
}) => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(
    null
  );
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { toasts, removeToast, success, error: errorToast } = useToast();

  // Universal invite link state
  const [inviteSlug, setInviteSlug] = useState<string | null>(null);
  const [slugLoading, setSlugLoading] = useState(true);

  useEffect(() => {
    loadInviteSlug();
  }, []);

  const loadInviteSlug = async () => {
    setSlugLoading(true);
    try {
      const response = await apiService.getVCInviteSlug();
      if (response.success && response.data) {
        setInviteSlug(response.data.invite_slug);
      }
    } catch (err) {
      console.error("Failed to load invite slug:", err);
    } finally {
      setSlugLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (!inviteSlug) return;

    // Use getBaseUrl to get the correct domain for current environment
    const baseUrl = getBaseUrl();
    const inviteLink = `${baseUrl}/${inviteSlug}/signup`;
    navigator.clipboard.writeText(inviteLink);
    success("Invite link copied to clipboard!");
  };

  const handleResendInvite = async (inviteId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      await apiService.resendPortfolioInvite(inviteId);
      success("Invitation resent successfully!");
    } catch (err: any) {
      errorToast(err.message || "Failed to resend invitation");
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      active: { label: "ACTIVE", className: styles.statusActive },
      pending_setup: {
        label: "PENDING SETUP",
        className: styles.statusPending,
      },
      invited: { label: "INVITED", className: styles.statusInvited },
    };
    return (
      statusMap[status.toLowerCase()] || {
        label: status.toUpperCase(),
        className: styles.statusDefault,
      }
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const stats = {
    total: companies.length,
    active: companies.filter((c) => c.status === "active").length,
    pending: companies.filter((c) => c.status === "pending_setup").length,
    invited: companies.filter((c) => c.status === "invited").length,
  };

  const filteredCompanies = companies.filter(
    (company) =>
      company.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.user_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <InlineSpinner />
        <span>Loading portfolio companies...</span>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Portfolio Companies</h1>
          <p className={styles.subtitle}>Manage and monitor your portfolio</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className={styles.inviteButton}
        >
          + Invite Company
        </button>
      </div>

      {/* Stats */}
      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>TOTAL COMPANIES</div>
          <div className={styles.statValue}>{stats.total}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>ACTIVE</div>
          <div className={`${styles.statValue} ${styles.statGreen}`}>
            {stats.active} •
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>PENDING SETUP</div>
          <div className={`${styles.statValue} ${styles.statYellow}`}>
            {stats.pending} •
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>AWAITING INVITE</div>
          <div className={`${styles.statValue} ${styles.statGray}`}>
            {stats.invited} •
          </div>
        </div>
      </div>

      {/* Search */}
      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search companies..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />
      </div>

      {/* Companies Table */}
      {filteredCompanies.length === 0 && !loading ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📋</div>
          <h3 className={styles.emptyTitle}>No portfolio companies yet</h3>
          <p className={styles.emptyDescription}>
            Invite your portfolio companies to start receiving automated updates
            with insights from their connected tools.
          </p>
          <button
            onClick={() => setShowInviteModal(true)}
            className={styles.emptyButton}
          >
            + Invite Your First Company
          </button>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>COMPANY</th>
                <th>STATUS</th>
                <th>INTEGRATIONS</th>
                <th>LAST UPDATE</th>
                <th>NEXT UPDATE</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((company) => {
                const statusInfo = getStatusBadge(company.status);
                return (
                  <tr
                    key={company.id}
                    onClick={() => setSelectedCompanyId(company.id)}
                    className={styles.tableRow}
                  >
                    <td>
                      <div className={styles.companyCell}>
                        <div className={styles.companyIcon}>
                          {company.company_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className={styles.companyName}>
                            {company.company_name}
                          </div>
                          {company.user_email && (
                            <div className={styles.companyEmail}>
                              {company.user_email}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${statusInfo.className}`}
                      >
                        • {statusInfo.label}
                      </span>
                    </td>
                    <td>
                      {company.status === "active" ? (
                        <span className={styles.integrationCount}>
                          {company.integrations_count} connected
                        </span>
                      ) : (
                        <span className={styles.textMuted}>—</span>
                      )}
                    </td>
                    <td>
                      <span className={styles.dateText}>
                        {formatDate(company.last_update)}
                      </span>
                    </td>
                    <td>
                      <span
                        className={
                          company.next_update
                            ? styles.dateTextOverdue
                            : styles.textMuted
                        }
                      >
                        {formatDate(company.next_update)}
                      </span>
                    </td>
                    <td>
                      {company.status === "invited" && (
                        <button
                          className={styles.resendButton}
                          onClick={(e) => handleResendInvite(company.id, e)}
                        >
                          ↻ Resend
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Portfolio Invite Link */}
      <div className={styles.inviteLinkSection}>
        <div className={styles.inviteLinkHeader}>
          <div>
            <h3 className={styles.inviteLinkTitle}>Portfolio Invite Link</h3>
            <p className={styles.inviteLinkDescription}>
              Share this link with anyone to invite them as a portfolio company
            </p>
          </div>
        </div>

        {slugLoading ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <InlineSpinner />
            <span style={{ color: "#888" }}>Loading invite link...</span>
          </div>
        ) : inviteSlug ? (
          <div className={styles.inviteLinkBox}>
            <input
              type="text"
              readOnly
              value={`${getBaseUrl()}/${inviteSlug}/signup`}
              className={styles.inviteLinkInput}
            />
            <button className={styles.copyButton} onClick={handleCopyLink}>
              Copy
            </button>
          </div>
        ) : (
          <div className={styles.errorMessage}>
            Invite link not available. Please complete your VC onboarding.
          </div>
        )}
      </div>

      {/* Modals */}
      {showInviteModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => setShowInviteModal(false)}
        >
          <div
            className={styles.modalContainer}
            onClick={(e) => e.stopPropagation()}
          >
            <InviteCompanyForm
              onSuccess={() => {
                setShowInviteModal(false);
                onReload();
              }}
              onCancel={() => setShowInviteModal(false)}
            />
          </div>
        </div>
      )}

      {selectedCompanyId && (
        <CompanyDetailModal
          companyId={selectedCompanyId}
          onClose={() => setSelectedCompanyId(null)}
        />
      )}
    </div>
  );
};

export default VCPortfolioPage;
